import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { ShipWheel, Users, MoveHorizontal, MessageCircle, Sparkles, X, ZoomIn, ChevronLeft, ChevronRight } from 'lucide-react';
import { getWhatsAppUrl } from '../utils/whatsapp';
import { useLanguage } from '../context/LanguageContext';
import { PREMIUM_BOATS } from '../data/premiumBoats';
export const PremiumFleet = () => {
  const {
    t
  } = useLanguage();
  const [lightbox, setLightbox] = useState<{
    image: string;
    name: string;
  } | null>(null);
  const [galleryIndex, setGalleryIndex] = useState<Record<string, number>>({});
  const advanceGallery = (boatId: string, gallery: string[], direction: 1 | -1) => {
    setGalleryIndex(prev => {
      const current = prev[boatId] ?? 0;
      const next = (current + direction + gallery.length) % gallery.length;
      return {
        ...prev,
        [boatId]: next
      };
    });
  };
  const handleDragEnd = (e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo, boatId: string, gallery: string[]) => {
    const swipeThreshold = 50;
    if (info.offset.x > swipeThreshold) {
      advanceGallery(boatId, gallery, -1);
    } else if (info.offset.x < -swipeThreshold) {
      advanceGallery(boatId, gallery, 1);
    }
  };
  useEffect(() => {
    if (!lightbox) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightbox]);
  const handleWhatsApp = (boatName: string) => {
    const message = t('wa.premium').replace('[BARCO]', boatName);
    window.open(getWhatsAppUrl(message), '_blank');
  };
  return <section id="premium" className="py-16 sm:py-20 md:py-24 bg-slate-50" data-id="element-463">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-464">
        {/* HEADER */}
        <div className="text-center mb-12 sm:mb-16" data-id="element-465">
          <div className="inline-flex items-center justify-center mb-4" data-id="element-466">
            <div className="bg-turquoise/10 text-turquoise border border-turquoise/20 font-bold uppercase text-xs sm:text-sm px-4 py-2 rounded-full tracking-wider" data-id="element-467">
              {t('premium.badge')}
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-navy mb-4 uppercase tracking-tight whitespace-pre-line" data-id="element-468">
            {t('premium.title')}
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 font-medium mb-4" data-id="element-469">
            {t('premium.subtitle')}
          </p>
          <p className="text-sm sm:text-base text-gray-500 max-w-2xl mx-auto leading-relaxed" data-id="element-470">
            {t('premium.description')}
          </p>
        </div>

        {/* GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10" data-id="element-471">
          {PREMIUM_BOATS.map((boat, idx) => {
          const isFeatured = boat.id === 'puravida';
          const specialBadge = isFeatured ? t('premium.boat.puravida.badge') : undefined;
          const note = boat.id === 'lagoon' ? t('premium.boat.lagoon.note') : undefined;
          return <motion.article key={boat.id} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            delay: idx * 0.1,
            duration: 0.6
          }} className={`relative rounded-[12px] overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300 flex flex-col group bg-navy ${isFeatured ? 'ring-2 ring-[#C9A84C]/60 shadow-[0_0_40px_rgba(201,168,76,0.25)]' : ''}`} data-id="element-472">
                {(() => {
              const currentIdx = galleryIndex[boat.id] ?? 0;
              const currentImage = boat.gallery?.[currentIdx] ?? boat.image;
              const hasGallery = !!boat.gallery && boat.gallery.length > 1;
              return <div className="relative w-full aspect-[4/3] sm:aspect-[3/2] overflow-hidden" data-id="element-473">
                      <button type="button" onClick={() => setLightbox({
                  image: currentImage,
                  name: boat.name
                })} aria-label={t('premium.aria.zoom')} className="absolute inset-0 w-full h-full overflow-hidden cursor-zoom-in focus:outline-none focus:ring-4 focus:ring-turquoise/60 z-0" data-id="element-474">
                        <AnimatePresence mode="wait" initial={false} data-id="element-475">
                          <motion.img key={currentImage} src={currentImage} alt={boat.name} loading={idx < 2 ? 'eager' : 'lazy'} fetchPriority={idx < 2 ? 'high' : 'auto'} initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.35
                    }} drag={hasGallery ? 'x' : false} dragConstraints={{
                      left: 0,
                      right: 0
                    }} dragElastic={0.2} onDragEnd={hasGallery ? (e, info) => handleDragEnd(e, info, boat.id, boat.gallery!) : undefined} className="absolute inset-0 w-full h-full object-cover" data-id="element-476" />
                        </AnimatePresence>
                      </button>

                      {/* Gradients */}
                      <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-black/60 to-transparent pointer-events-none z-0" data-id="element-477" />
                      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-none z-0" data-id="element-478" />

                      {/* CAROUSEL ARROWS */}
                      {hasGallery && <>
                          <button type="button" onClick={e => {
                    e.stopPropagation();
                    advanceGallery(boat.id, boat.gallery!, -1);
                  }} aria-label={t('premium.aria.prev')} className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 z-20 bg-white/15 hover:bg-white/30 backdrop-blur-md text-white rounded-full p-2 sm:p-2.5 transition-all hover:scale-110 shadow-lg" data-id="element-479">
                            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-480" />
                          </button>
                          <button type="button" onClick={e => {
                    e.stopPropagation();
                    advanceGallery(boat.id, boat.gallery!, 1);
                  }} aria-label={t('premium.aria.next')} className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 z-20 bg-white/15 hover:bg-white/30 backdrop-blur-md text-white rounded-full p-2 sm:p-2.5 transition-all hover:scale-110 shadow-lg" data-id="element-481">
                            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-482" />
                          </button>

                          {/* Dots */}
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex gap-1.5 pointer-events-none" data-id="element-483">
                            {boat.gallery!.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === currentIdx ? 'bg-white w-5' : 'bg-white/50 w-1.5'}`} data-id="element-484" />)}
                          </div>
                        </>}

                      {/* Zoom hint */}
                      <button type="button" onClick={() => setLightbox({
                  image: currentImage,
                  name: boat.name
                })} aria-label={t('premium.aria.zoom')} className="absolute top-4 right-4 sm:hidden bg-white/20 backdrop-blur-md text-white rounded-full p-2 z-20 hover:bg-white/30 transition-colors" data-id="element-485">
                        <ZoomIn className="w-4 h-4" data-id="element-486" />
                      </button>

                      {/* CONTENT OVERLAY */}
                      <div className="absolute inset-0 p-4 sm:p-6 flex flex-col justify-between z-10 pointer-events-none" data-id="element-487">
                        <div className="flex justify-between items-start gap-3" data-id="element-488">
                          <div className="flex flex-wrap gap-1.5" data-id="element-489">
                            <div className="bg-[#0A1F44]/90 backdrop-blur-sm text-white rounded-md px-1.5 py-1 flex flex-col items-center justify-center min-w-[42px] sm:min-w-[48px] shadow-lg border border-white/10" data-id="element-490">
                              <span className="text-[8px] sm:text-[9px] font-bold uppercase text-white/80 leading-none" data-id="element-491">
                                {t('premium.captain')}
                              </span>
                              <ShipWheel className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-turquoise my-0.5" data-id="element-492" />
                              <span className="text-[8px] sm:text-[9px] font-bold uppercase leading-none" data-id="element-493">
                                {t('premium.captainLabel')}
                              </span>
                            </div>

                            <div className="bg-[#0A1F44]/90 backdrop-blur-sm text-white rounded-md px-1.5 py-1 flex flex-col items-center justify-center min-w-[42px] sm:min-w-[48px] shadow-lg border border-white/10" data-id="element-494">
                              <Users className="w-3 h-3 text-turquoise" data-id="element-495" />
                              <span className="text-sm sm:text-base font-black leading-none my-0.5" data-id="element-496">
                                {boat.specs.capacity}
                              </span>
                              <span className="text-[8px] sm:text-[9px] font-bold uppercase leading-none" data-id="element-497">
                                {t('premium.max')}
                              </span>
                            </div>

                            <div className="bg-[#0A1F44]/90 backdrop-blur-sm text-white rounded-md px-1.5 py-1 flex flex-col items-center justify-center min-w-[42px] sm:min-w-[48px] shadow-lg border border-white/10" data-id="element-498">
                              <MoveHorizontal className="w-3 h-3 text-turquoise" data-id="element-499" />
                              <span className="text-sm sm:text-base font-black leading-none my-0.5" data-id="element-500">
                                {boat.specs.length}
                              </span>
                              <span className="text-[8px] sm:text-[9px] font-bold uppercase leading-none" data-id="element-501">
                                {t('premium.meters')}
                              </span>
                            </div>

                            {specialBadge && <div className="bg-[#C9A84C]/90 backdrop-blur-sm text-white rounded-md px-2 py-1 flex flex-col items-center justify-center shadow-lg border border-white/20" data-id="element-502">
                                <Sparkles className="w-3 h-3 mb-0.5" data-id="element-503" />
                                <span className="text-[8px] sm:text-[9px] font-black uppercase tracking-wider text-center leading-tight whitespace-pre-line" data-id="element-504">
                                  {specialBadge.split(' ').join('\n')}
                                </span>
                              </div>}
                          </div>

                          <h3 className="text-lg sm:text-2xl md:text-3xl font-black text-white uppercase text-right drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] tracking-wide shrink-0" data-id="element-505">
                            {boat.name}
                          </h3>
                        </div>

                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 sm:gap-6 mt-auto" data-id="element-506">
                          <div className="flex-1 max-w-md order-2 sm:order-1 w-full sm:w-auto pointer-events-auto" data-id="element-507">
                            <button onClick={() => handleWhatsApp(boat.name)} className="w-full sm:w-auto bg-[#25D366] hover:bg-[#1ebd5a] text-white px-3 py-2 rounded-full font-black uppercase tracking-wider flex items-center justify-center gap-1.5 text-[10px] sm:text-xs transition-transform hover:scale-105 shadow-lg" data-id="element-508">
                              <MessageCircle className="w-3.5 h-3.5" data-id="element-509" />
                              {t('premium.reserve')}
                            </button>
                          </div>

                          <div className="flex flex-col items-start sm:items-end gap-1.5 order-1 sm:order-2 w-full sm:w-auto" data-id="element-510">
                            <div className="flex flex-wrap sm:flex-nowrap justify-start sm:justify-end gap-2 w-full" data-id="element-511">
                              {boat.prices.map((price, i) => <div key={i} className="bg-[#E63946] text-white rounded-lg px-2 py-1 flex flex-col items-center shadow-lg border border-white/10 min-w-[58px] sm:min-w-[64px]" data-id="element-512">
                                  <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider opacity-90 leading-none" data-id="element-513">
                                    {price.duration}
                                  </span>
                                  <span className="text-xs sm:text-sm font-black leading-none mt-0.5" data-id="element-514">
                                    {price.amount}
                                  </span>
                                </div>)}
                            </div>
                            {note && <span className="text-[11px] sm:text-xs font-medium text-white/80 drop-shadow-md mt-1 ml-1 sm:ml-0" data-id="element-515">
                                {note}
                              </span>}
                          </div>
                        </div>
                      </div>
                    </div>;
            })()}
              </motion.article>;
        })}
        </div>
      </div>

      {/* LIGHTBOX */}
      <AnimatePresence data-id="element-516">
        {lightbox && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} onClick={() => setLightbox(null)} className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 cursor-zoom-out" role="dialog" aria-modal="true" aria-label={`Foto ampliada de ${lightbox.name}`} data-id="element-517">
            <button type="button" onClick={e => {
          e.stopPropagation();
          setLightbox(null);
        }} aria-label={t('premium.aria.close')} className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white rounded-full p-3 transition-colors" data-id="element-518">
              <X className="w-6 h-6" data-id="element-519" />
            </button>

            <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-10" data-id="element-520">
              <div className="bg-white/10 backdrop-blur-md text-white font-black uppercase tracking-wider px-4 py-2 rounded-full text-sm sm:text-base" data-id="element-521">
                {lightbox.name}
              </div>
            </div>

            <motion.img initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} src={lightbox.image} alt={lightbox.name} onClick={e => e.stopPropagation()} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl cursor-default" data-id="element-522" />
          </motion.div>}
      </AnimatePresence>
    </section>;
};